import React from 'react';
import millify from 'millify';
import { gql } from '@soundxyz/gql-string';
import { useStableCallback } from '@components-shared/src/hooks/useStableCallback';
import { LandingRankedListItem } from '@components/Landing/shared/LandingRankedList/LandingRankedListItem';
import { useLikeRelease } from '@components/Likes/useLikeRelease';
import { AddToQueueMenuItem } from '@components/basics/Dropdowns/MenuItems/AddToQueueMenuItem';
import { AddToShelfMenuItem } from '@components/basics/Dropdowns/MenuItems/AddToShelfMenuItem';
import { CopyLinkMenuItem } from '@components/basics/Dropdowns/MenuItems/CopyLinkMenuItem';
import { ShareMenuItem } from '@components/basics/Dropdowns/MenuItems/ShareMenuItem';
import { Button } from '@components/main-components/button/Button';
import { CollectModal } from '@components/modals/Collect/CollectModal';
import { ReleaseMintsModal } from '@components/modals/ReleaseMintsModal';
import { ModalType, useModal } from '@context/modal';
import { ReleaseType, type ViralSoundsRankedListItemFragment } from '@graphql/reactQuery';

import { useCopy } from '@hooks/useCopy';
import { usePlayButton } from '@hooks/usePlayButton';
import { useTimeUntil } from '@hooks/useTimeUntil';
import { trackClickEvent } from '@lib/analytics';
import { useAudioController } from '@store/audio/AudioController';
import { setUpNextTrackIds } from '@store/audio/AudioQueue';
import {
  getNextTrackIds,
  getStaticCoverImage,
  stopPropagationAndForwardClick,
} from '@utils/helpers';
import { webappUriPath } from '@utils/url';

/*#__PURE__*/ gql(/* GraphQL */ `
  fragment ViralSoundsRankedListItem on ReleaseChartRank {
    id
    release {
      id
      title
      titleSlug
      type
      artist {
        id
        name
        soundHandle
        webappUri
      }
      track {
        id
      }
      coverImage {
        id
        url
      }
      staticCoverImage {
        id
        url
      }
      totalMinted
      supplyCutoffTimestamp
      webappUri
      ...CollectModal
    }
    trendingIndicator
    currentRank
  }
`);

export function ViralSoundsRankedListItem({
  chartRank,
  allReleases,
}: {
  chartRank: ViralSoundsRankedListItemFragment;
  allReleases: ViralSoundsRankedListItemFragment['release'][];
}) {
  /**
   * Hooks
   */
  const { activeTrackId, playing } = useAudioController();
  const { onPlayClick } = usePlayButton({
    isAlbum: chartRank.release.type === ReleaseType.Album,
    trackId: chartRank.release.track.id,
  });
  const { isLiked, onClick: onLikeClicked } = useLikeRelease({
    id: chartRank.release.id,
    source: 'Chart',
  });
  const { copy } = useCopy({
    text: chartRank.release.webappUri,
    successMessage: 'Link copied to clipboard',
  });
  const { openModal } = useModal();
  const [endsInText, urgency] = useTimeUntil(chartRank.release.supplyCutoffTimestamp);

  /**
   * When the play button is hit we also want to
   * setup the queue
   */
  const playAndSetupNextTrackIds = useStableCallback(async () => {
    await onPlayClick();
    setUpNextTrackIds(
      getNextTrackIds({
        trackId: chartRank.release.track.id,
        releases: allReleases,
      })(),
    );
  });

  const hasAudioAccess = chartRank.release.type !== 'ALBUM';

  return (
    <LandingRankedListItem
      trendingIndicator={chartRank.trendingIndicator}
      title={{ text: chartRank.release.title, href: webappUriPath(chartRank.release.webappUri) }}
      subtitle={{
        text: chartRank.release.artist.name,
        href: webappUriPath(chartRank.release.artist.webappUri),
      }}
      secondarySubtitle={{
        text: `${millify(chartRank.release.totalMinted)} mint${
          chartRank.release.totalMinted === 1 ? `` : `s`
        }`,
        onClick: () => {
          openModal(ModalType.RELEASE_MINTS, {
            body: <ReleaseMintsModal releaseId={chartRank.release.id} />,
          });
        },
      }}
      tertiarySubtitle={
        endsInText
          ? {
              text: `Ends ${endsInText}`,
              alert: urgency === 'high',
            }
          : undefined
      }
      image={getStaticCoverImage({
        coverImage: chartRank.release.coverImage.url,
        staticCoverImage: chartRank.release.staticCoverImage?.url,
      })}
      imageRadius="rounded-md"
      id={chartRank.id}
      rank={chartRank.currentRank}
      onClickCell={playAndSetupNextTrackIds}
      isPlaying={chartRank.release.track.id === activeTrackId && playing}
      actionButton={
        <Button
          label="Collect"
          variant="tertiary"
          size="XS"
          onClick={stopPropagationAndForwardClick(() => {
            openModal(ModalType.COLLECT, {
              body: (
                <CollectModal
                  release={chartRank.release}
                  playlistReferral={null}
                  collectButtonSource="Landing Viral Sounds"
                />
              ),
            });
          })}
          className="transition-all duration-200 hover:bg-white hover:shadow-tertiary"
        />
      }
      likeButton={{
        liked: isLiked,
        onClick: onLikeClicked,
      }}
      dropdownContent={
        <>
          <AddToShelfMenuItem releaseId={chartRank.release.id} source="Card" />
          <AddToQueueMenuItem
            trackId={chartRank.release.track.id}
            disabled={!chartRank.release.track.id || !hasAudioAccess}
            emitAnalytics={() => {
              trackClickEvent({
                event: 'Add to Queue Clicked',
                properties: {
                  associationId: chartRank.release.id,
                  associationType: 'Release',
                  sourceComponent: 'Chart',
                },
              });
            }}
          />
          <CopyLinkMenuItem onClick={copy} />
          <ShareMenuItem releaseId={chartRank.release.id} source="Chart" />
        </>
      }
    />
  );
}
